<script setup>
import Dropdown from '@/Components/Dropdown.vue';
import { onMounted } from 'vue';
import { router } from '@inertiajs/vue3';

import { locale, watchLocale } from '@/Localization.js';
import { isLoaded, loadLanguageAsync, trans, transChoice } from 'laravel-vue-i18n';

const langs = ['fr', 'en', 'es', 'nl', 'it'];
const flags = {
    'fr': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAALCAMAAABBPP0LAAAAbFBMVEVzldTg4ODS0tLxDwDtAwDjAADD0uz39/fy8vL3k4nzgna4yOixwuXu7u7s6+zn5+fyd2rvcGPtZljYAABrjNCpvOHrWkxegsqfs93NAADpUUFRd8THAABBa7wnVbERRKa8vLyxsLCoqKigoKClCvcsAAAAXklEQVR4AS3JxUEAQQAEwZo13Mk/R9w5/7UERJCIGIgj5qfRJZEpPyNfCgJTjMR1eRRnJiExFJz5Mf1PokWr/UztIjRGQ3V486u0HO55m634U6dMcf0RNPfkVCTvKjO16xHA8miowAAAAABJRU5ErkJggg==',
    'en': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAALCAMAAABBPP0LAAAAt1BMVEWSmb66z+18msdig8La3u+tYX9IaLc7W7BagbmcUW+kqMr/q6n+//+hsNv/lIr/jIGMnNLJyOP9/fyQttT/wb3/////aWn+YWF5kNT0oqz0i4ueqtIZNJjhvt/8gn//WVr/6+rN1+o9RKZwgcMPJpX/VFT9UEn+RUX8Ozv2Ly+FGzdYZrfU1e/8LS/lQkG/mbVUX60AE231hHtcdMb0mp3qYFTFwNu3w9prcqSURGNDaaIUMX5FNW5wYt7AAAAAjklEQVR4AR3HNUJEMQCGwf+L8RR36ajR+1+CEuvRdd8kK9MNAiRQNgJmVDAt1yM6kSzYVJUsPNssAk5N7ZFKjVNFAY4co6TAOI+kyQm+LFUEBEKKzuWUNB7rSH/rSnvOulOGk+QlXTBqMIrfYX4tSe2nP3iRa/KNK7uTmWJ5a9+erZ3d+18od4ytiZdvZyuKWy8o3UpTVAAAAABJRU5ErkJggg==',
    'es': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAALCAMAAABBPP0LAAAAflBMVEX/AAD9AAD3AADxAADrAAD/eXn9bGz8YWH8WVn6UVH5SEj5Pz/3NDT0Kir9/QD+/nL+/lT18lDt4Uf6+j/39zD39yf19R3n5wDxflXsZ1Pt4Y3x8zr0wbLs1NXz8xPj4wD37t3jmkvsUU/Bz6nrykm3vJ72IiL0FBTyDAvhAABEt4UZAAAAX0lEQVR4AQXBQUrFQBBAwXqTDkYE94Jb73+qfwVRcYxVQRBRToiUfoaVpGTrtdS9SO0Z9FR9lVy/g5c99+dKl30N5uxPuviexXEc9/msC7TOkd4kHu/Dlh4itCJ8AP4B0w4Qwmm7CFQAAAAASUVORK5CYII=',
    'nl': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAALCAMAAABBPP0LAAAAY1BMVEX/AAD8AADzAADqAAD/fYz+dYT6aHr7XG36VGb6R1f4L0H/ECz+mKXw8fH9/f36+vr19vbW1tbS0tIAG493n89cjcZNgsA/eLkzcbUpaLAcYKwAAFEAA4ANU6UAADsAAHgAAGcSgiW+AAAAS0lEQVR4AQXBiQ3CQBAAMc/dConQf688SoIdBIIyQiR9R0tCuM2rNxHpMqsDIWiBEE4NgnBiHhVJcs48P0uSjXsPl4hLmH2EHzb4A7DPDGppZMSwAAAAAElFTkSuQmCC',
    'it': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAALCAIAAAD5gJpuAAAAG0lEQVR42mNgmOSGjP5jgHPa5siIYVTDCNEAACtNNQLXWJ25AAAAAElFTkSuQmCC'
};

const setLocale = (s) => {
    if (locale.value != s) {
        locale.value = s;
        localStorage.locale = s; // fr|en
        updateLocale(s);
    }
};

const resetLocale = () => {
    if (locale.value) {
        locale.value = null;
        localStorage.removeItem('locale');
        updateLocale(null);
    }
};

const updateLocale = async (lang) => {
    loadLanguageAsync(lang);

    let url = window.location.href.replace(/\/[a-z]{2}\//, `/${lang}/`);

    await watchLocale();

    if (/\/[a-z]{2}\//.test(window.location.pathname)) {
        router.get(url, {},
            {
                preserveState: true,
                preserveScroll: true,
            });
    } else {
        console.error('window.location.reload()', 'updateLocale()', window.location.pathname);
        window.location.reload()
    }
    // TODO? call route('language.switch')
};

const init = () => {
    if (isLoaded()) {
        setLocale(locale.value);
    } else {
        setTimeout(init, 400);
    }
};

onMounted(() => {
    init()
});
</script>

<template>
    <Dropdown align="left" width="32">
        <template #trigger>
            <div class="flex gap-2 items-center cursor-pointer hover:text-blue-600 hover:dark:text-blue-400" :class="{ 'text-blue-600 dark:text-blue-400': !!locale, 'text-gray-800 dark:text-white': !locale }" :title="locale">
                <svg class="w-6 h-6" viewBox="0 0 64 64">
                    <path d="M45.07 15.56c1.03.8 2.26-.73 1.24-1.56-15-12.05-37.4-.95-37.31 18-.25 12.38 10.62 23.25 23 23a23.07 23.07 0 0 0 23-23c0-5.28-1.73-10.23-5-14.31-.82-1.02-2.37.22-1.56 1.24a21 21 0 0 1 2.84 4.74h-9.97c-.87-5.3-2.55-9.61-4.71-12.16 3.08.69 5.95 2.04 8.47 4.05M27.4 11.52c-2.16 2.55-3.84 6.85-4.71 12.15h-9.96A21 21 0 0 1 27.4 11.52M39.96 32q0 3.38-.37 6.33H24.4a52 52 0 0 1 0-12.66H39.6q.36 2.95.37 6.33M32 53c-2.91 0-5.98-4.85-7.3-12.67h14.6C37.98 48.15 34.9 53 32 53m-9.6-14.67H11.99c-1.3-4-1.3-8.69 0-12.66h10.43c-.5 3.97-.5 8.7 0 12.66m-6.84 6.74a21 21 0 0 1-2.84-4.74h9.97c.87 5.3 2.55 9.61 4.71 12.16-4.6-1-8.9-3.7-11.84-7.42m21.04 7.4c2.16-2.54 3.84-6.84 4.71-12.14h9.96A21 21 0 0 1 36.6 52.48m15.42-26.8c1.3 4 1.3 8.69 0 12.66H41.59a53 53 0 0 0 0-12.66zm-12.72-2H24.7c2.98-16.78 11.62-16.78 14.6 0"/>
                    <path d="M32 4C16.56 4 4 16.56 4 32c.03 1.32 1.97 1.31 2 0C6 17.66 17.66 6 32 6c34.5 1.43 34.48 50.58 0 52-4.8 0-9.48-1.32-13.55-3.81-1.13-.67-2.16 1-1.05 1.7C35.72 67.34 60.2 53.63 60 32 60 16.56 47.44 4 32 4"/>
                    <path d="M5.19 58.81a3.9 3.9 0 0 0 4.39.86l4.13-1.76c1.2-.54.44-2.33-.78-1.84L8.8 57.83a2 2 0 0 1-2.63-2.63l3.71-8.73c.1-.3.08-.63-.07-.91a26 26 0 0 1-3.37-8.77c-.25-1.28-2.18-.94-1.96.36a28 28 0 0 0 3.37 8.99l-3.52 8.28a3.9 3.9 0 0 0 .86 4.4"/>
                </svg>
                <!-- <img :src="flags[locale]" alt="" width="16" height="11">
                {{ locale }} -->
            </div>
        </template>

        <template #content>
            <button v-for="lang in langs" @click="setLocale(lang)" class="flex gap-2 items-center w-full px-4 py-2 text-left text-sm leading-5 hover:bg-gray-100 dark:hover:bg-[#273E44]/50 focus:outline-none focus:bg-gray-100 dark:focus:bg-[#273E44]/50 transition duration-150 ease-in-out" :class="{ 'text-blue-600 dark:text-blue-400': locale == lang, 'text-gray-700 dark:text-gray-300': locale != lang }">
                <img :src="flags[lang]" alt="" width="16" height="11">
                {{ lang }}
            </button>
            <!-- <button @click="resetLocale()" class="block w-full px-4 py-2 text-left text-sm leading-5 hover:bg-gray-100 dark:hover:bg-[#273E44]/50 focus:outline-none focus:bg-gray-100 dark:focus:bg-[#273E44]/50 transition duration-150 ease-in-out" :class="{ 'text-blue-600 dark:text-blue-400': !locale, 'text-gray-700 dark:text-gray-300': !!locale }">
                System
            </button> -->
        </template>
    </Dropdown>
</template>